import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, Dropdown, SnoozeModal } from "components";
import { useInterventionAppendSnoozeMutation } from "modules/AppointmentDetails/components/Interventions/components/InterventionActionSelector/hooks";
import "modules/AppointmentDetails/components/Interventions/components/InterventionActionSelector/InterventionActionSelector.scss";
import { useUnlinkInterventionMutation } from "modules/AppointmentDetails/components/Interventions/hooks";
import { useAppointmentDealer, useAppointmentLocation } from "modules/AppointmentDetails/hooks";

import type { Intervention, Snooze } from "models";
import type { InterventionSnoozeData } from "modules/AppointmentDetails/components/Interventions/components/Intervention";

type InterventionActionSelectorProps = {
  intervention: Intervention;
};

export const InterventionActionSelector = ({ intervention }: InterventionActionSelectorProps) => {
  const { t } = useTranslation();

  const appointmentLocation = useAppointmentLocation();
  const appointmentDealer = useAppointmentDealer();
  const { mutateAsync: interventionAppendSnooze, isPending: isInterventionAppendSnoozePending } = useInterventionAppendSnoozeMutation();
  const { mutateAsync: unlinkIntervention, isPending: isUnlinkInterventionPending } = useUnlinkInterventionMutation();

  const [snoozeModalData, setSnoozeModalData] = useState<InterventionSnoozeData | null>(null);
  const [showDisconnectInterventionModal, setShowDisconnectInterventionModal] = useState(false);

  const isInterventionSnoozed = Boolean(intervention.snoozed) || Boolean(intervention.snooze_history?.some(item => item.intervention_id === intervention.id));

  const handleConfirmDisconnectIntervention = async () => {
    await unlinkIntervention(intervention.id);
    setShowDisconnectInterventionModal(false);
  };

  const handleSnoozeIntervention = () => {
    if (intervention.snoozed || intervention.mechanic_fixed) return;

    setSnoozeModalData({
      ...intervention,
      question_result_id: null,
      intervention_id: intervention.id,
      snooze_status_id: null,
      snooze_history: intervention.snooze_history || []
    });
  };

  const handleInterventionAppendSnooze = async (snooze: Snooze) => {
    await interventionAppendSnooze(snooze);
  };

  return (
    <>
      <div className="InterventionActionSelector">
        <Dropdown
          disabled={intervention.mechanic_fixed || isInterventionSnoozed}
          className="action-dropdown"
          floating
          icon={null}
          direction="left"
          trigger={<Icon className={isInterventionSnoozed ? "clock grey" : "plus grey"} />}
        >
          <DropdownMenu className="dropdown-menu">
            {!isInterventionSnoozed && (
              <DropdownItem onClick={handleSnoozeIntervention}>
                <Icon className="clock" />
                {t("v8_snooze", "Snooze")}
              </DropdownItem>
            )}

            {intervention.question_result_id && (
              <DropdownItem onClick={() => setShowDisconnectInterventionModal(true)}>
                <Icon className="link simple slash" />
                {t("v8_disconnect_checklist", "Disconnect Checklist")}
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={showDisconnectInterventionModal}
        isLoading={isUnlinkInterventionPending}
        handleConfirm={handleConfirmDisconnectIntervention}
        handleCancel={() => setShowDisconnectInterventionModal(false)}
        confirmMsg={t("v8_unlink_intervention_confirmation_message", "Are you sure you want to disconnect the checklist item from this intervention?")}
      />
      {snoozeModalData && (
        <SnoozeModal
          dealer={appointmentDealer}
          location={appointmentLocation}
          open={!!snoozeModalData}
          onClose={() => setSnoozeModalData(null)}
          data={snoozeModalData}
          onAppendSnooze={handleInterventionAppendSnooze}
          isAppendSnoozeLoading={isInterventionAppendSnoozePending}
        />
      )}
    </>
  );
};
